import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getTransactionLedger } from '../../actions/transactionLedger';
import { 
    Container, 
    Row, 
    Col, 
    Card, 
    Form, 
    Button, 
    Table, 
    Badge, 
    Alert, 
    Spinner,
    Toast,
    ToastContainer,
    Pagination
} from 'react-bootstrap';
import "bootstrap/dist/css/bootstrap.min.css";

const Ledger = ({location, transactions, getTransactionLedger, pageList, pages, activePage, loading}) => {

    const initialState = {
        vendingMachineId: '',
        transactionId: '',
        phone: '',
        startDate: '',
        endDate: '',
        latest: true
    };

    const [formData, setFormData] = useState(initialState);
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [queryString, setQueryString] = useState("page=1&latest=true");
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVariant, setToastVariant] = useState('success');
    const [specificUserId, setSpecificUserId] = useState("");

    const showAlert = (message, variant = 'danger') => {
        setToastMessage(message);
        setToastVariant(variant);
        setShowToast(true);
    };

    useEffect(() => {
        if(transactions === undefined){
            let query = queryString;
            if(location && location.search){
                let phoneNumber = location.search.split("=");
                if(phoneNumber.length === 2 && phoneNumber[1].length === 10){
                    query += "&phone=" + phoneNumber[1];
                    setSpecificUserId(phoneNumber[1]);
                }
            }
            getTransactionLedger(query);
        } else {
            processTransactionData(transactions);
            setCurrentPage(activePage || 1);
        }
    }, [location, transactions, getTransactionLedger]);

    const processTransactionData = (transactionData) => {
        const processedData = transactionData.map(transaction => ({
            _id: transaction._id,
            id: transaction.id,
            containerId: transaction.containers && transaction.containers.length > 0 ? transaction.containers.toString() : transaction.containerId || 'N/A',
            transactionId: transaction.transactionId,
            time: new Date(transaction.time).toLocaleString(),
            rawTime: new Date(transaction.time),
            vendingMachineId: transaction.vendingMachine ? transaction.vendingMachine._id : 'N/A',
            vendingMachineName: transaction.vendingMachine ? transaction.vendingMachine.name : 'Deleted',
            userName: transaction.user ? transaction.user.name : "Deleted User",
            userId: transaction.user ? transaction.user._id : "deleted",
            requestStatus: transaction.requestStatus,
            isContainerWorking: transaction.isContainerWorking ? "YES" : "NO",
            transactionType: transaction.transactionType
        }));

        // Sort by latest if needed
        if (formData.latest) {
            processedData.sort((a, b) => b.rawTime - a.rawTime);
        } else {
            processedData.sort((a, b) => a.rawTime - b.rawTime);
        }

        setData(processedData);
    };




    const {
        vendingMachineId,
        startDate,
        endDate,
        phone,
        transactionId,
        latest
    } = formData;

    const onChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    }

    const onSubmit = e => {
        e.preventDefault();
        
        if (formData.startDate && formData.endDate) {
            const startDate = new Date(formData.startDate).getTime();
            const endDate = new Date(formData.endDate).getTime();
            if(endDate < startDate){
                showAlert("End date must be after start date");
                return;
            }
        }
        
        // Build query string for filters
        let newQueryString = `page=1&latest=${formData.latest}`;
        if(formData.startDate && formData.endDate){
            newQueryString += `&startDate=${formData.startDate}&endDate=${formData.endDate}`;
        }
        if(formData.vendingMachineId){
            newQueryString += `&vendingMachine=${formData.vendingMachineId}`;
        }
        if(formData.transactionId){
            newQueryString += `&transactionId=${formData.transactionId}`;
        }
        if(formData.phone){
            newQueryString += `&phone=${formData.phone}`;
        }
        
        setQueryString(newQueryString);
        setCurrentPage(1);
        getTransactionLedger(newQueryString);
    };

    const handleSort = (field) => {
        const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortDirection(direction);
        
        const sortedData = [...data].sort((a, b) => {
            if (direction === 'asc') {
                return a[field] > b[field] ? 1 : -1;
            }
            return a[field] < b[field] ? 1 : -1;
        });
        setData(sortedData);
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return <i className="fas fa-sort text-muted ms-1"></i>;
        return sortDirection === 'asc' 
            ? <i className="fas fa-sort-up text-primary ms-1"></i>
            : <i className="fas fa-sort-down text-primary ms-1"></i>;
    };

    const clearFilters = () => {
        setFormData(initialState);
        setSpecificUserId("");
        const newQueryString = "page=1&latest=true";
        setQueryString(newQueryString);
        setCurrentPage(1);
        getTransactionLedger(newQueryString);
        showAlert("Filters cleared successfully!", "info");
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > pages) return;
        
        const newQueryString = queryString.replace(`page=${currentPage}`, `page=${page}`);
        setQueryString(newQueryString);
        setCurrentPage(page);
        getTransactionLedger(newQueryString);
    };

    const renderPagination = () => {
        if (!pages || pages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(pages, startPage + maxVisiblePages - 1);

        // First page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }

        // Page numbers
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Last page
        if (endPage < pages) {
            if (endPage < pages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            items.push(
                <Pagination.Item key={pages} onClick={() => handlePageChange(pages)}>
                    {pages}
                </Pagination.Item>
            );
        }

        return (
            <div className="d-flex justify-content-center mt-4">
                <Pagination>
                    <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {items}
                    <Pagination.Next
                        disabled={currentPage === pages}
                        onClick={() => handlePageChange(currentPage + 1)}
                    />
                </Pagination>
            </div>
        );
    };

    const getRequestStatusBadge = (status) => {
        switch(status?.toLowerCase()) {
            case 'pending':
                return <Badge bg="warning">Pending</Badge>;
            case 'completed':
                return <Badge bg="success">Completed</Badge>;
            case 'failed':
                return <Badge bg="danger">Failed</Badge>;
            case 'cancelled':
                return <Badge bg="secondary">Cancelled</Badge>;
            default:
                return <Badge bg="info">{status || 'Unknown'}</Badge>;
        }
    };

    const getTransactionTypeBadge = (type) => {
        switch(type?.toLowerCase()) {
            case 'purchase':
                return <Badge bg="primary">Purchase</Badge>;
            case 'refund':
                return <Badge bg="warning">Refund</Badge>;
            case 'deposit':
                return <Badge bg="success">Deposit</Badge>;
            default:
                return <Badge bg="secondary">{type || 'Unknown'}</Badge>;
        }
    };

    const renderTransactionTable = () => {
        if (!data || data.length === 0) {
            return (
                <Alert variant="info" className="text-center">
                    <i className="fas fa-info-circle me-2"></i>
                    No transaction data found. Try adjusting your filters.
                </Alert>
            );
        }

        return (
            <div className="table-responsive">
                <Table striped bordered hover className="mb-0">
                    <thead className="table-dark">
                        <tr>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('id')}
                            >
                                ID
                                {getSortIcon('id')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('transactionId')}
                            >
                                Transaction ID
                                {getSortIcon('transactionId')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('userName')}
                            >
                                User
                                {getSortIcon('userName')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('vendingMachineName')}
                            >
                                Vending Machine
                                {getSortIcon('vendingMachineName')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('containerId')}
                            >
                                Container ID
                                {getSortIcon('containerId')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('requestStatus')}
                            >
                                Status
                                {getSortIcon('requestStatus')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('isContainerWorking')}
                            >
                                Container Working
                                {getSortIcon('isContainerWorking')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('transactionType')}
                            >
                                Type
                                {getSortIcon('transactionType')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('time')}
                            >
                                Time
                                {getSortIcon('time')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, index) => (
                            <tr key={row._id || index}>
                                <td>
                                    <span className="text-truncate d-inline-block" style={{maxWidth: '100px'}} title={row.id}>
                                        {row.id}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-truncate d-inline-block" style={{maxWidth: '150px'}} title={row.transactionId}>
                                        {row.transactionId}
                                    </span>
                                </td>
                                <td>
                                    <span className={`${row.userName === 'Deleted User' ? 'text-muted fst-italic' : ''}`}>
                                        {row.userName}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-truncate d-inline-block" style={{maxWidth: '150px'}} title={row.vendingMachineName}>
                                        {row.vendingMachineName}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-truncate d-inline-block" style={{maxWidth: '120px'}} title={row.containerId}>
                                        {row.containerId}
                                    </span>
                                </td>
                                <td>
                                    {getRequestStatusBadge(row.requestStatus)}
                                </td>
                                <td>
                                    <Badge bg={row.isContainerWorking === 'YES' ? 'success' : 'danger'}>
                                        {row.isContainerWorking}
                                    </Badge>
                                </td>
                                <td>
                                    {getTransactionTypeBadge(row.transactionType)}
                                </td>
                                <td>
                                    <small className="text-muted">
                                        {new Date(row.rawTime).toLocaleDateString()}<br/>
                                        {new Date(row.rawTime).toLocaleTimeString()}
                                    </small>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <Container fluid className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="text-primary mb-0">
                    <i className="fas fa-exchange-alt me-2"></i>
                    Transaction Ledger
                </h2>
                {specificUserId && (
                    <Badge bg="info">
                        <i className="fas fa-user me-1"></i>
                        Phone Specific View
                    </Badge>
                )}
            </div>

            {/* Filters Card */}
            <Card className="shadow-sm mb-4">
                <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-filter me-2"></i>
                        Search Filters
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={onSubmit}>
                        <Row className="g-3">
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-cog me-2"></i>
                                        Vending Machine ID
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="vendingMachineId"
                                        value={vendingMachineId}
                                        onChange={onChange}
                                        placeholder="Enter vending machine ID"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-receipt me-2"></i>
                                        Transaction ID
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="transactionId"
                                        value={transactionId}
                                        onChange={onChange}
                                        placeholder="Enter transaction ID"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-phone me-2"></i>
                                        Phone Number
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        value={phone}
                                        onChange={onChange}
                                        placeholder="Enter phone number"
                                        maxLength={10}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-calendar me-2"></i>
                                        Start Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="startDate"
                                        value={startDate}
                                        onChange={onChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-calendar me-2"></i>
                                        End Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="endDate"
                                        value={endDate}
                                        onChange={onChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-sort me-2"></i>
                                        Sort Order
                                    </Form.Label>
                                    <Form.Select
                                        name="latest"
                                        value={latest}
                                        onChange={onChange}
                                    >
                                        <option value="true">Newest First</option>
                                        <option value="false">Oldest First</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={12} className="d-flex align-items-end">
                                <div className="d-flex gap-2 w-100">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={loading}
                                        className="flex-fill"
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Searching...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-search me-2"></i>
                                                Apply Filters
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline-secondary"
                                        onClick={clearFilters}
                                        disabled={loading}
                                    >
                                        <i className="fas fa-times me-2"></i>
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* Summary Cards */}
            {data.length > 0 && (
                <Row className="g-3 mb-4">
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-receipt text-primary fa-2x mb-2"></i>
                                <h5 className="card-title">Total Records</h5>
                                <h3 className="text-primary">{data.length}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-check-circle text-success fa-2x mb-2"></i>
                                <h5 className="card-title">Completed</h5>
                                <h3 className="text-success">
                                    {data.filter(row => row.requestStatus?.toLowerCase() === 'completed').length}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-clock text-warning fa-2x mb-2"></i>
                                <h5 className="card-title">Pending</h5>
                                <h3 className="text-warning">
                                    {data.filter(row => row.requestStatus?.toLowerCase() === 'pending').length}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-times-circle text-danger fa-2x mb-2"></i>
                                <h5 className="card-title">Failed</h5>
                                <h3 className="text-danger">
                                    {data.filter(row => row.requestStatus?.toLowerCase() === 'failed').length}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Results Card */}
            <Card className="shadow-sm">
                <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="fas fa-table me-2"></i>
                        Transaction Records
                    </h5>
                    <div className="d-flex align-items-center gap-3">
                        {pages > 1 && (
                            <small className="text-light">
                                Page {currentPage} of {pages}
                            </small>
                        )}
                        {data.length > 0 && (
                            <Badge bg="light" text="dark">
                                {data.length} record{data.length !== 1 ? 's' : ''}
                            </Badge>
                        )}
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <div className="mt-2">Loading transaction data...</div>
                        </div>
                    ) : (
                        <>
                            {renderTransactionTable()}
                            {/* Pagination */}
                            {pages > 1 && (
                                <div className="p-3 border-top bg-light">
                                    {renderPagination()}
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Toast Notifications */}
            <ToastContainer position="top-end" className="p-3">
                <Toast
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    delay={5000}
                    autohide
                    bg={toastVariant}
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toastVariant === 'success' ? 'Success' : toastVariant === 'info' ? 'Info' : 'Alert'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className={toastVariant === 'danger' ? '' : 'text-white'}>
                        {toastMessage}
                    </Toast.Body>
                </Toast>
            </ToastContainer>

            <style>{`
                .cursor-pointer {
                    cursor: pointer;
                }
                .user-select-none {
                    user-select: none;
                }
                .table th:hover {
                    background-color: #495057 !important;
                }
            `}</style>
        </Container>
    )
}

Ledger.propTypes = {
    getTransactionLedger: PropTypes.func.isRequired,
    transactions: PropTypes.array,
    pages: PropTypes.number,
    activePage: PropTypes.number,
    pageList: PropTypes.array,
    location: PropTypes.object,
    loading: PropTypes.bool
}

const mapStateToProps = state => ({
    transactions: state.transactionLedger.transactions,
    pages: state.transactionLedger.pages,
    pageList: state.transactionLedger.pageList,
    activePage: state.transactionLedger.activePage,
    loading: state.transactionLedger.loading
})

export default connect( mapStateToProps, { getTransactionLedger } )( Ledger );